import React, {useCallback} from 'react';
import useTranslation from "~/app/hooks/useTranslation";
import classes from "./style.module.scss";
import Button from '~/app/common/Button';
import {useParams} from 'react-router-dom';
import {Machine, Project} from "~/types";
import {route} from "~/app/helpers/route";
import {MACHINES_ROUTES} from "~/machines/Router";
import {MACHINES_DETAILS_ROUTES} from "~/machines/Details/Router";
import {useHistory} from "react-router";
import {useStoreActions, useStoreState} from "~/store";
import {notify} from "~/app/helpers/notify";
import { generateGUID } from '~/app/helpers/guid';
import moment from 'moment';
import { PERIODIC_CHECK_DRAFT_STATUS } from '~/app/data/statuses/periodicCheck';
import { InspectionTypes } from '~/periodicCheck/store';

export type FooterComponent = React.FC

const Footer: FooterComponent = () => {
    const {t} = useTranslation("periodicCheck");
    const history = useHistory();
    const {id, projectId} = useParams<{ id: Machine['id'], projectId: Project['id'] }>();
    const isChecksDataUpdated = useStoreState(state => state.periodicCheck.isChecksDataUpdated);
    const checksData = useStoreState(state => state.periodicCheck.checksData);
    const inspectionType = useStoreState(state => state.periodicCheck.inspectionType);
    const loading = useStoreState(state => state.periodicCheck.updating);
    const machine = useStoreState(state => state.machine.data);
    const checkEquipment = useStoreState(state => state.periodicCheck.checkEquipment);
    const dailyCheckId = useStoreState(state => state.periodicCheck.dailyCheckId);
    const saveMachineId = checkEquipment?.id ?? machine.id;
    const updatePeriodicChecks = useStoreActions(actions => actions.periodicCheck.update);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const MachineFetchAction = useStoreActions(actions => actions.machine.fetchMachine);
    const MachineOfflineFetchAction = useStoreActions(actions => actions.machine.fetchOfflineMachine);
    const applyLocalLastPeriodicCheck = useStoreActions(actions => actions.machine.applyLocalLastPeriodicCheck);
    const user = useStoreState(state => state.user.data);

    const buildPayload = useCallback((isDraft: number) => ({
        machineId: saveMachineId,
        projectId,
        inspectionType,
        checks: checksData,
        guid: generateGUID(),
        isDraft,
        ...(dailyCheckId && { id: dailyCheckId }),
    }), [saveMachineId, projectId, inspectionType, checksData, dailyCheckId]);

    const back = useCallback(() => {
        const destination = inspectionType !== InspectionTypes.ACCEPTANCE_INSPECTION && machine.linkedMachines && machine.linkedMachines.length > 0
            ? route(MACHINES_DETAILS_ROUTES.periodicCheckEquipments, { id, projectId })
            : route(MACHINES_ROUTES.details, { id, projectId });
        history.push(destination);
    }, [id, projectId, history, machine.linkedMachines, inspectionType]);

    const saveDraftClickHandler = useCallback(() => {
        updatePeriodicChecks(buildPayload(1)).then(async (response) => {
            const draftId = response?.checkData?.id ?? response?.id ?? dailyCheckId;
            applyLocalLastPeriodicCheck({
                machineId: saveMachineId,
                inspectionType,
                lastCheck: {
                    id: String(draftId ?? saveMachineId),
                    userId: user.id,
                    userName: user.name,
                    date: moment().format('YYYY-MM-DD'),
                    status: PERIODIC_CHECK_DRAFT_STATUS,
                },
            });
            if (!netWorkStatus) {
                await MachineFetchAction(id);
            } else {
                await MachineOfflineFetchAction(id);
            }
            notify( draftId ? t('draftSaved') : t(`${inspectionType}.success`), 'success');
        });
    }, [updatePeriodicChecks, buildPayload, applyLocalLastPeriodicCheck, saveMachineId, inspectionType, dailyCheckId, user.id, user.name, netWorkStatus, MachineFetchAction, MachineOfflineFetchAction, id, t]);

    const submit = useCallback(() => {
        updatePeriodicChecks(buildPayload(0)).then(async () => {
            if (!netWorkStatus) {
                await MachineFetchAction(id);
            } else {
                await MachineOfflineFetchAction(id);
            }
            applyLocalLastPeriodicCheck({
                machineId: saveMachineId,
                inspectionType,
                lastCheck: {
                    id: String(saveMachineId),
                    userId: user.id,
                    userName: user.name,
                    date: new Date().toISOString()
                }
            });
            const destination = inspectionType !== InspectionTypes.ACCEPTANCE_INSPECTION && machine.linkedMachines && machine.linkedMachines.length > 0
                ? route(MACHINES_DETAILS_ROUTES.periodicCheckEquipments, { id, projectId })
                : route(MACHINES_ROUTES.details, { id, projectId });
            history.push(destination);
            notify(t(`${inspectionType}.success`), 'success');
        });
    }, [updatePeriodicChecks, buildPayload, netWorkStatus, MachineFetchAction, MachineOfflineFetchAction, applyLocalLastPeriodicCheck, saveMachineId, machine.linkedMachines, projectId, inspectionType, user.id, user.name, id, history, t]);

    return (
        <div className={classes['footer']}>
            <Button className={`${classes['abort']}`} onClick={back}> {t('cancel')} </Button>
            <Button
                loading={loading}
                className={classes['temporary']}
                onClick={saveDraftClickHandler}
            > {t('temporary')} </Button>
            <Button
                loading={loading}
                className={classes['submit']}
                onClick={submit}
                disabled={!isChecksDataUpdated}
            >  {t('complete')}  </Button>
        </div>
    );
};

export default Footer
